/*
 * File: input-number.ts
 * File Created: 2024-01-14 10:54:46
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-11-21 05:50:00
 * Modified By: Val Liu
 * -----
 */

import { Component, Prop, Watch } from "vue-property-decorator";
import { PropInnateSync } from "@root/core/vue-props";
import { BaseInputComponent } from "../core/lib/base-input-component";
import { NumberHelper, ObjectHelper } from "@root/core";
import { persistent } from "@root/core/decorators/persistent";

/**
 * rule:
 * 1. the first element is always the default / stored value.
 * 2. after the first value, every element should contain "ratio".
 * 3. todo, possible to deal with temperature F / C.
 *
 * options:
 * [
 *     {
 *         label: "w_SquareMeter",
 *     },
 *     {
 *         label: "w_Tsubo",
 *         ratio: 0.3025,
 *     },
 * ]
 */

interface IInputUnitNumberOptionUnit {
    label: string;
    ratio?: number;
}
type IInputUnitNumberOption = IInputUnitNumberOptionUnit[];

@Component
export default class InputUnitNumber extends BaseInputComponent {
    @PropInnateSync("value", {
        type: Number,
        validator: (value: number) => {
            if (isNaN(value)) {
                console.error(
                    `<${value}> is not a valid value for <iv-input-unit-number>.`,
                );
            }
            return true;
        },
        default: null,
    })
    innateValue!: number | null;

    @Prop({ type: Number, default: 3 })
    decimal!: number;

    /// name / value pair of the select / options
    @Prop({ type: Array, required: true })
    options!: IInputUnitNumberOption;

    /// default unit that matches the options `label`
    @Prop({ type: String, required: false })
    unit?: string;

    get description(): string {
        const results: string[] = [];
        const value = this.innateValue;
        const options = this.innateOptions;
        if (value && options) {
            for (const option of options) {
                const { label, ratio } = option;
                if (label === this.selectedUnit) continue;
                results.push(
                    `= ${NumberHelper.toFixedNumber(value * (ratio ?? 1), 3)} ${this.$_(
                        label,
                    )}`,
                );
            }
        }
        return results.join("<BR/>");
    }

    /// private helpers
    @persistent()
    selectedUnit: any = null;

    @Watch("unit", { immediate: true })
    onUnitUpdated(newUnit, oldUnit) {
        if (ObjectHelper.equals(newUnit, oldUnit)) return;
        this.selectedUnit = newUnit;
    }
    private innateOptions?: IInputUnitNumberOption;
    @Watch("options", { immediate: true })
    onOptionsUpdated(newOptions, oldOptions) {
        if (ObjectHelper.equals(newOptions, oldOptions)) return;
        this.innateOptions = ObjectHelper.deepClone(newOptions);
        this.selectedUnit ??= newOptions[0].label;
    }
    get parsedOptions() {
        return this.options?.reduce((final, value) => {
            final[this.$_(value.label)] = value.label;
            return final;
        }, {});
    }

    /// handler: the innateValue should not change,
    /// within `disableUpdateInterval` ms after selectedUnit updated.
    private disableUpdateInterval: number = 10;
    disableUpdateValue: boolean = false;
    @Watch("selectedUnit")
    onSelectedUnitUpdated(newOption, oldOption) {
        this.disableUpdateValue = true;
        setTimeout(() => {
            this.disableUpdateValue = false;
        }, this.disableUpdateInterval);
    }
    get calculatedValue() {
        if (this.innateValue == null) return null;
        return NumberHelper.toFixedNumber(
            this.innateValue *
                (this.options.find((value) => value.label === this.selectedUnit)?.ratio ??
                    1),
            this.decimal,
        );
    }
    onUpdateValue($event) {
        if (this.disableUpdateValue) return;
        if ($event == null) {
            this.innateValue = $event;
            return;
        }
        this.innateValue =
            $event /
            (this.options.find((value) => value.label === this.selectedUnit)?.ratio ?? 1);
    }
}
